import React, { useState, useEffect } from "react";
/** @jsx jsx */
import { Box, jsx, useThemeUI } from "theme-ui";
import {
  Container,
  Flex,
  FlexGrid,
  Heading,
  Text,
  AspectRatio,
  Select,
  Button,
} from "../../components/core";
import Page from "../../components/Page";
import Contact from "../../components/Contact";
import { Parallax } from "../../components/animate";
import { alpha } from "@theme-ui/color";
import { motion } from "framer-motion";
import { graphql, useStaticQuery } from "gatsby";
import { CardContent } from "../../components/event/CardContent";
import { Card } from "../../components/Card";
import { Vague } from "../../components/svg";
import { useResponsiveValue } from "../../hooks/breakpoints";
import { InView } from "react-intersection-observer";
import SEO from "../../components/SEO";
import { getImage, StaticImage } from "gatsby-plugin-image";
import moment from "../../helpers/moment";

const queryData = graphql`
  query EventsQueryData {
    markdownRemark(fields: { slug: { eq: "/evenements/" } }) {
      id
      frontmatter {
        image {
          childImageSharp {
            gatsbyImageData(quality: 100, placeholder: NONE, layout: FULL_WIDTH)
          }
        }
        intro {
          text
          title
        }
      }
    }
    allMarkdownRemark(
      sort: { order: DESC, fields: [frontmatter___date] }
      filter: {
        frontmatter: { templateKey: { eq: "event" }, visible: { eq: true } }
      }
    ) {
      edges {
        node {
          id
          fields {
            slug
          }
          frontmatter {
            numero
            title
            place
            templateKey
            type
            date(formatString: "DD MMMM YYYY", locale: "fr")
            image {
              childImageSharp {
                gatsbyImageData(
                  placeholder: NONE
                  layout: CONSTRAINED
                  aspectRatio: 1.666
                  width: 900
                )
              }
            }
          }
        }
      }
    }
  }
`;

const Events = () => {
  const data = useStaticQuery(queryData);
  const { edges: events } = data.allMarkdownRemark;
  const frontmatter = data.markdownRemark.frontmatter;
  const { theme } = useThemeUI();
  const value = useResponsiveValue([12, 6, 4]);
  const [isPast, setIsPast] = useState(false);

  const totalEvents = [
    ...events.filter(
      ({ node: event }) => event.frontmatter.type === "Permanent"
    ),
    ...events.filter(
      ({ node: event }) => event.frontmatter.type === "Ponctuel"
    ),
  ]
    .filter(({ node: event }) => {
      // Parse both dates to the same granularity (day level)
      const eventDate = moment(event.frontmatter.date, "DD MMMM YYYY").startOf('day');
      const today = moment().startOf('day');
      
      const dateIsAfter = eventDate.isAfter(today);
      const dateIsSame = eventDate.isSame(today);

      return isPast
        ?  !dateIsAfter && !dateIsSame && event.frontmatter.type === "Ponctuel"
        : dateIsAfter || dateIsSame || event.frontmatter.type === "Permanent";
    })
    .sort(({ node: eventA }, { node: eventB }) => {
      // Parse dates using Moment.js with French locale
      const dateA = moment(eventA.frontmatter.date, "DD MMMM YYYY");
      const dateB = moment(eventB.frontmatter.date, "DD MMMM YYYY");
      return isPast ? dateB.diff(dateA) : dateA.diff(dateB); // or dateA.diff(dateB)
    });



  const years = [
    ...new Set(
      totalEvents
        .map(({ node: event }) => {
          const match = event.frontmatter.date.match(/\d{4}/); // Cherche une année
          return match ? match[0] : null; // Retourne l'année ou null
        })
        .filter((year) => year) // Supprime les valeurs null ou undefined
    ),
  ].map((year) => ({
    value: year,
    label: year,
  }));

  const [currentYear, setCurrentYear] = useState(years[0]);
  const variants = {
    visible: (i) => ({
      opacity: 1,
      transition: {
        delay: (i % (12 / value)) * 0.3,
      },
    }),
    hidden: { opacity: 0 },
  };

  useEffect(() => {
    if(isPast) {
      setCurrentYear(years[0]);
    }
  }, [isPast]);

  return (
    <Page sx={{ backgroundColor: "background3" }}>
      <SEO
        title="Les événements"
        description="Retrouvez l'ensemble des activités de Clean my Calanques !"
      />
      <AspectRatio ratio={[1440 / 630, 1440 / 480, 1440 / 315]}>
        <Parallax
          alt="Image en-tête évenements"
          image={getImage(frontmatter.image)}
          backgroundColor={alpha("primary", 0.25)(theme)}
        >
          <Flex
            sx={{
              width: "100%",
              height: "100%",
              py: 0,
              px: [2, null, 0],
            }}
          >
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1.6, delay: 0.8 }}
              sx={{ textAlign: "center" }}
            >
              <Heading as="h1" color="white">
                Ramassages
              </Heading>
            </motion.div>
          </Flex>
        </Parallax>
      </AspectRatio>
      <Container sx={{ paddingTop: [8, null, 10] }}>
        <Box>
          <Flex direction={["column", "row"]} positions={[["flex-start", "flex-start"], ["space-between", "flex-start"]]}>
            <Box>
              <Heading as="h2" color="secondary" sx={{ marginBottom: 3 }}>
                {frontmatter.intro.title}
              </Heading>
              <Text>{frontmatter.intro.text}</Text>
            </Box>
            <Flex direction={["row", "column"]} gap={1} sx={{ marginTop: 3 }}>
              <Text>Co-financé par</Text>
              <StaticImage src="./VilleMarseille.webp" 
                    alt="Logo Ville Marseille"
                    sx={{
                      width: "160px",
                    }}
                />
            </Flex>
        </Flex>
          <Vague
            color="primary"
            sx={{
              marginTop: 6,
              marginBottom: [8, null, 5],
            }}
          />
        </Box>
      </Container>
      <Container sx={{ paddingBottom: [8, null, 13] }}>
        <Flex
          positions={["space-between", "flex-start"]}
          sx={{ marginBottom: 6 }}
        >
          <Flex gap={2} wrap="wrap" positions={["flex-start", "center"]}>
            <Button
              variant="badge"
              active={!isPast}
              outline
              onClick={() => {
                setIsPast(false);
              }}
            >
              À venir
            </Button>
            <Button
              variant="badge"
              active={isPast}
              outline
              onClick={() => {
                setIsPast(true);
              }}
            >
              Passés
            </Button>
          </Flex>
          {isPast && (
            <Flex gap={1} positions={["flex-start", "center"]}>
              <Text>Parcourez les ramassages de</Text>
              <Box
                sx={{
                  minWidth: "76px",
                  flexShrink: 0,
                }}
              >
                <Select
                  placeholder="Choisissez une année"
                  options={years}
                  value={currentYear}
                  onChange={setCurrentYear}
                  defaultValue={years[0]}
                />
              </Box>
            </Flex>
          )}
        </Flex>

        {totalEvents && (
          <FlexGrid positions={["flex-start", "stretch"]} columns={[12, 6, 4]}>
            {totalEvents
              .filter(({ node: event }) => {
                if (!isPast) return true;
                if (event.frontmatter.type === "Permanent") {
                  return currentYear?.value === years[0].value;
                }
                const year = event.frontmatter.date.match(/\d{4}/)[0]; // Match 4 consecutive digits
                return year === currentYear?.value;
              })
              .map(({ node: event }, index) => (
                <Box key={event.id}>
                  <InView triggerOnce threshold={0.3}>
                    {({ inView, ref }) => (
                      <Card
                        ref={ref}
                        id={event.frontmatter.numero}
                        custom={index}
                        variants={variants}
                        animate={inView ? "visible" : "hidden"}
                        to={event.fields.slug}
                      >
                        <Card.Image
                          image={getImage(event.frontmatter.image)}
                          imageLoading={index < 6 ? "eager" : "lazy"}
                          alt={`Image card ${event.frontmatter.title}`}
                        />
                        <CardContent
                          numero={event.frontmatter.numero}
                          type={event.frontmatter.type}
                          date={event.frontmatter.date}
                          title={event.frontmatter.title}
                          place={event.frontmatter.place}
                        />
                      </Card>
                    )}
                  </InView>
                </Box>
              ))}
          </FlexGrid>
        )}
      </Container>
      <Contact />
    </Page>
  );
};

export default Events;
